/*!
 * latency-probe.js — acoustic round-trip latency measurement core.
 *
 * Plays a short sine burst into an output node and watches an input
 * MediaStream for the burst's arrival. Detection is FREQUENCY-SELECTIVE
 * (Goertzel power at the burst frequency) and NOISE-CALIBRATED (threshold
 * is +12 dB over a per-attempt measured baseline at that same frequency),
 * so ambient broadband noise neither masks a quiet echo nor fakes a hit.
 *
 * History: the previous in-blade detector compared max(|byte-128|) of
 * getByteTimeDomainData against a threshold of 140 — a value that scale
 * CANNOT REACH (|byte-128| tops out at 128 even at full-scale clipping).
 * 90 days of telemetry on both shards: 968 runs, 968 failures, zero
 * successful measurements ever. This module replaces it; its detection
 * behaviour is pinned by tests/playwright/bluetooth-latency-probe.mjs
 * (synthetic in-graph loopback with known delays — run it before touching
 * the thresholds here).
 *
 * API:
 *   window.LatencyProbe.measure(opts) -> Promise<result>
 *     opts.ctx         AudioContext (required, running)
 *     opts.micStream   MediaStream to listen on (required)
 *     opts.out         AudioNode to play into (default ctx.destination)
 *     opts.attempts    number of bursts (default 5)
 *     opts.beepMs      burst length ms (default 120)
 *     opts.level       burst gain 0..1 (default 0.6)
 *     opts.timeoutMs   per-attempt listen window after burst (default 2000)
 *     opts.isCancelled function -> true to abort between frames
 *     opts.onProgress  function(attemptIndex, foundMsOrNull)
 *
 *   window.LatencyProbe.micFailure(peak) -> 'silent' | 'not_heard' — the ONE
 *     rule for "the mic is dead" vs "the mic did not hear us" (every probe
 *     that plays into a speaker and listens with a mic asks it)
 *   window.LatencyProbe.peakDb(peak) -> whole dBFS for telemetry
 *
 *   result = {
 *     ms          number  — median round-trip ms, or -1 when no attempt heard
 *     samples     array   — per-attempt detected ms (successes only)
 *     failure     string  — '' | 'silent' (mic gives digital silence) | 'not_heard'
 *     diag = {
 *       micPeak   number  — max |sample| seen on mic across the run (0..1)
 *       micPeakDb number  — the same peak in whole dBFS (−200 = digital zero)
 *       baseDb    number  — median noise floor at probe frequencies, dBFS
 *       ctxState  string  — AudioContext.state at the end
 *       outLatMs  number  — ctx.outputLatency*1000 (0 when unsupported)
 *       baseLatMs number  — ctx.baseLatency*1000
 *     }
 *   }
 *
 * Classic script (ES5, no imports) — safe for @light() + the ES2019 guard.
 */
(function () {
    'use strict';

    /** Goertzel amplitude^2 of frequency `freq` over buf[from..to). For a
     *  pure sine of amplitude A at the bin this returns ~A^2 (normalized). */
    function goertzelA2(buf, from, to, sampleRate, freq) {
        var n = to - from;
        if (n <= 8) return 0;
        var k = Math.round(n * freq / sampleRate);
        if (k < 1) k = 1;
        var w = 2 * Math.PI * k / n;
        var coeff = 2 * Math.cos(w);
        var s1 = 0, s2 = 0, s0, i;
        for (i = from; i < to; i++) {
            s0 = buf[i] + coeff * s1 - s2;
            s2 = s1; s1 = s0;
        }
        var p = s1 * s1 + s2 * s2 - coeff * s1 * s2; // |X(k)|^2 = (A*n/2)^2
        return p / (n * n / 4);
    }

    function median(arr) {
        if (!arr.length) return -1;
        var s = arr.slice().sort(function (a, b) { return a - b; });
        var m = s.length >> 1;
        return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    }

    function nextFrame() {
        // rAF with a 50 ms timer fallback: rAF freezes in background tabs,
        // and a user flipping away mid-measure must get a clean timeout
        // instead of a promise that never settles.
        return new Promise(function (r) {
            var done = false;
            var fin = function () { if (!done) { done = true; r(); } };
            requestAnimationFrame(fin);
            setTimeout(fin, 50);
        });
    }
    function wait(ms) {
        return new Promise(function (r) { setTimeout(r, ms); });
    }

    /* Is the MICROPHONE dead, or did it just not hear US? The one rule —
     * SpeakerProbe and RecordingLatency ask it too (docs/why/primitives/mic-flat.md).
     *
     *   'silent'    the mic delivers digital silence: Android's microphone
     *               access toggle off, a hardware mute, another app holding
     *               the device. Nothing it says about a speaker means anything.
     *   'not_heard' the mic hears the room but never our signal: output muted,
     *               routed to Bluetooth, or the speaker makes no sound.
     *
     * The line used to be peak <= 0.008 — one step of the 8-bit byte analyser
     * the first detector read (1/128), carried into float data. With echo
     * cancellation, noise suppression and AGC off (as every probe opens the
     * mic) a LIVE phone mic in a quiet room peaks far below it. Field, RU,
     * 20 days: of 631 /speaker-cleaner checks called 'silent' on phones, 484
     * peaked at 0.001–0.008 — a working mic told «не передаёт звук» while the
     * speaker was what stayed silent; /bluetooth-test measured latency 43
     * times with a peak under 0.008. Lab (tests/playwright/speaker-probe.mjs, mode quietroom):
     * fake capture at −50 / −60 / −75 dBFS was 'silent' under the old line.
     *
     * −80 dBFS is about three steps of 16-bit capture: below it the mic gives
     * zeros or quantisation, above it there is a room. */
    var MIC_FLAT_PEAK = 1e-4;
    function micFailure(peak) { return peak < MIC_FLAT_PEAK ? 'silent' : 'not_heard'; }
    /** Peak as whole dBFS, for telemetry: rounding the linear peak to three
     *  decimals turned everything under −66 dBFS into 0. Digital zero → −200. */
    function peakDb(peak) { return peak > 0 ? Math.round(20 * Math.log10(peak)) : -200; }

    /** One burst attempt. Returns detected ms or null. Mutates diag. */
    function runAttempt(ctx, analyser, out, freq, opts, diag) {
        var HOP = 256;
        var fft = analyser.fftSize;
        var hops = Math.floor(fft / HOP);
        var buf = new Float32Array(fft);
        var sr = ctx.sampleRate;

        return (function baseline() {
            // ~300 ms of ambient at the burst frequency -> noise floor.
            // MUST use the same 256-sample hop window as detection below:
            // white-noise power per Goertzel bin scales with window length
            // (2048-window baseline reads ~9 dB QUIETER than the 256-hop
            // detector sees), so a full-window baseline silently eats most
            // of the +12 dB margin and false-positives in broadband noise.
            var hopA2s = [], target = 18, frames = 0;
            return (function loop() {
                if (opts.isCancelled()) return Promise.resolve(null);
                analyser.getFloatTimeDomainData(buf);
                var pk = 0, i, v;
                for (i = 0; i < fft; i++) { v = Math.abs(buf[i]); if (v > pk) pk = v; }
                if (pk > diag.micPeak) diag.micPeak = pk;
                for (i = 0; i < hops; i++) {
                    hopA2s.push(goertzelA2(buf, i * HOP, (i + 1) * HOP, sr, freq));
                }
                if (++frames < target) return nextFrame().then(loop);
                return Promise.resolve(median(hopA2s));
            })();
        })().then(function (baseA2) {
            if (baseA2 === null || opts.isCancelled()) return null;
            diag._baseA2s.push(Math.max(baseA2, 1e-12));

            // Threshold: +12 dB over baseline, but never below an absolute
            // floor (~-66 dBFS amplitude) so DC/quantization noise can't arm it.
            var thr = Math.max(baseA2 * 15.85, 2.5e-7);

            // Schedule the burst at a known context time -> known perf time.
            var t0 = ctx.currentTime + 0.08;
            var startPerf = performance.now() + (t0 - ctx.currentTime) * 1000;
            var osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq;
            var g = ctx.createGain();
            var beepS = opts.beepMs / 1000;
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(opts.level, t0 + 0.005);
            g.gain.setValueAtTime(opts.level, t0 + beepS - 0.01);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + beepS);
            osc.connect(g); g.connect(out);
            osc.start(t0); osc.stop(t0 + beepS + 0.02);

            var deadline = startPerf + opts.beepMs + opts.timeoutMs;

            return (function listen() {
                if (opts.isCancelled()) return cleanup(null);
                var nowP = performance.now();
                if (nowP > deadline) return cleanup(null);
                analyser.getFloatTimeDomainData(buf);
                var pk = 0, i, v;
                for (i = 0; i < fft; i++) { v = Math.abs(buf[i]); if (v > pk) pk = v; }
                if (pk > diag.micPeak) diag.micPeak = pk;

                // Per-hop Goertzel: two consecutive hot hops = tone present
                // for >=10 ms. Hop start position inside the buffer converts
                // to "how long ago" -> sub-frame onset.
                var run = 0;
                for (i = 0; i < hops; i++) {
                    var a2 = goertzelA2(buf, i * HOP, (i + 1) * HOP, sr, freq);
                    if (a2 > thr) {
                        run++;
                        if (run >= 2) {
                            var onsetHop = i - 1;
                            var agoMs = (fft - onsetHop * HOP) / sr * 1000;
                            var ms = (nowP - agoMs) - startPerf;
                            return cleanup(ms);
                        }
                    } else {
                        run = 0;
                    }
                }
                return nextFrame().then(listen);
            })();

            function cleanup(val) {
                try { osc.disconnect(); } catch (e) {}
                try { g.disconnect(); } catch (e) {}
                return Promise.resolve(val);
            }
        });
    }

    function measure(opts) {
        var ctx = opts.ctx;
        var attempts = opts.attempts || 5;
        var o = {
            beepMs: opts.beepMs || 120,
            level: opts.level || 0.6,
            timeoutMs: opts.timeoutMs || 2000,
            isCancelled: opts.isCancelled || function () { return false; },
            onProgress: opts.onProgress || function () {}
        };
        var out = opts.out || ctx.destination;

        var source = ctx.createMediaStreamSource(opts.micStream);
        var analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);

        var diag = { micPeak: 0, _baseA2s: [] };
        var samples = [];
        var chain = Promise.resolve();

        for (var a = 0; a < attempts; a++) {
            (function (idx) {
                chain = chain.then(function () {
                    if (o.isCancelled()) return;
                    var freq = 1000 + idx * 200;
                    return runAttempt(ctx, analyser, out, freq, o, diag).then(function (ms) {
                        if (ms !== null && ms > -25) {
                            // Clock fuzz can nudge a genuinely-instant loop a
                            // few ms negative; clamp. Anything far negative is
                            // a detector fault, drop it.
                            samples.push(Math.max(ms, 0));
                            o.onProgress(idx, ms);
                        } else {
                            o.onProgress(idx, null);
                        }
                        return wait(250);
                    });
                });
            })(a);
        }

        return chain.then(function () {
            try { source.disconnect(); } catch (e) {}
            try { analyser.disconnect(); } catch (e) {}
            var baseDb = -100;
            if (diag._baseA2s.length) {
                baseDb = Math.round(10 * Math.log10(median(diag._baseA2s)));
            }
            var failure = '';
            if (!samples.length) failure = micFailure(diag.micPeak);
            return {
                ms: samples.length ? Math.round(median(samples)) : -1,
                samples: samples.map(function (s) { return Math.round(s); }),
                failure: failure,
                diag: {
                    micPeak: Math.round(diag.micPeak * 1000) / 1000,
                    micPeakDb: peakDb(diag.micPeak),
                    baseDb: baseDb,
                    ctxState: ctx.state,
                    outLatMs: Math.round((ctx.outputLatency || 0) * 1000),
                    baseLatMs: Math.round((ctx.baseLatency || 0) * 10000) / 10
                }
            };
        });
    }

    window.LatencyProbe = {
        measure: measure, _goertzelA2: goertzelA2,
        micFailure: micFailure, peakDb: peakDb, MIC_FLAT_PEAK: MIC_FLAT_PEAK
    };
})();
